import {BaseQueryFn, QueryDefinition} from '@reduxjs/toolkit/dist/query';
import {LazyQueryTrigger} from '@reduxjs/toolkit/src/query/react/buildHooks';
import {
    useLazySearchMaterialsQuery,
    MaterialSearchRequest,
    useLazySearchDoorsQuery,
    useLazySearchMaterialEdgesQuery,
    useLazyGetMatchedEdgeQuery,
    useLazySearchMaterialEdgesByCritriaQuery,
} from 'components/customer/Materials/store/materialApi';
import {useEffect, useRef} from 'react';
import {store} from 'store/customer';
import {
    finishLoading,
    startLoading,
} from 'components/customer/Materials/store/materialSlice';

type triggerType<T> = LazyQueryTrigger<
    QueryDefinition<MaterialSearchRequest, BaseQueryFn, string, T>
>;

interface ExtraProps {
    isLoading: boolean;
    isFetching: boolean;
}

const useAbortQuery = <T>(
    trigger: triggerType<T>,
    {isLoading, isFetching}: ExtraProps
) => {
    const abortHandler = useRef<() => void>();

    const abort = () => {
        if (typeof abortHandler.current == 'function') {
            abortHandler.current();
            setTimeout(() => store.dispatch(finishLoading()), 500);
        }
    };

    const search = (params: MaterialSearchRequest) => {
        abort();

        const request = trigger(params, true);
        // NOTE: This is actually returned by the trigger function.
        // Not sure why this is throwing this warning.
        // check https://redux-toolkit.js.org/rtk-query/api/created-api/hooks#uselazyquery
        // eslint-disable-next-line @typescript-eslint/unbound-method -- read comment above
        abortHandler.current = request.abort;

        return request;
    };

    useEffect(() => {
        if (isLoading || isFetching) {
            store.dispatch(startLoading());
        } else {
            setTimeout(() => store.dispatch(finishLoading()), 500);
        }
    }, [isLoading, isFetching]);

    return {search, abort};
};

export const useSearchMaterialsQueryWithAbort = () => {
    const [trigger, props] = useLazySearchMaterialsQuery();
    const handlers = useAbortQuery(trigger, props);

    return {
        ...props,
        ...handlers,
    };
};

export const useSearchDoorsQueryWithAbort = () => {
    const [trigger, props] = useLazySearchDoorsQuery();
    const handlers = useAbortQuery(trigger, props);

    return {
        ...props,
        ...handlers,
    };
};

export const useSearchEdgeQueryWithAbort = () => {
    const [trigger, props] = useLazySearchMaterialEdgesQuery();
    const handlers = useAbortQuery(trigger, props);

    return {
        ...props,
        ...handlers,
    };
};

export const useGetMatchedEdgeWithAbort = () => {
    const [trigger, props] = useLazyGetMatchedEdgeQuery();
    const handlers = useAbortQuery(trigger, props);

    return {
        ...props,
        ...handlers,
    };
};

export const useSearchMaterialEdgesByCritriaWithAbort = () => {
    const [trigger, props] = useLazySearchMaterialEdgesByCritriaQuery();
    const handlers = useAbortQuery(trigger, props);

    return {
        ...props,
        ...handlers,
    };
};
